import React, {Component, ReactNode, RefObject} from 'react'
import styled from 'styled-components'
import {MeetingCommentFile} from '../model/MeetingCommentFile'
import {Colors, FontWeights} from '../../../styledComponents/Constants'
import {FlexContainer, FullWidthLeftRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {getLinkIconComponent, IconBox} from '../AssocDirectorCommon'
import {getFileExtension} from '../../../__NoAutomatedTests/common/util/Natutil'
import {faFileMedical, faTimes} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import { Box, OutlinedInput } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {AttachmentFileName} from './MeetingCommentComponent'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import {HKConstants} from '../../../HKConstants'
import {AttachmentFile} from './AdminMeetingCommentComponent'

export interface InputError {
    title: string,
    messages: string[],
    fileNameList?: string[],
}

interface Props {
    submitButtonLabel: string
    onClickSubmitButton: () => Promise<void>
    onClickCancelButton?: () => void
    onChange: (
        content: string,
        commentFileList: MeetingCommentFile[],
        deleteFileNumberList: number[],
        inputError?: InputError,
    ) => void
    content: string
    commentFileList: MeetingCommentFile[]
    deleteFileNumberList: number[]
    inputError?: InputError
}

class AdminMeetingCommentInputForm extends Component<Props> {
    commentFileRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.commentFileRef = React.createRef()

        this.handleChangeComment = this.handleChangeComment.bind(this)
        this.handleClickAddFileButton = this.handleClickAddFileButton.bind(this)
        this.handleChangeFileInput = this.handleChangeFileInput.bind(this)
        this.handleClickDeleteFileButton = this.handleClickDeleteFileButton.bind(this)
    }

    handleChangeComment(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.props.onChange(
            event.target.value,
            this.props.commentFileList,
            this.props.deleteFileNumberList,
            this.props.inputError,
        )
    }

    handleClickAddFileButton(): void {
        const fileUpload = document.getElementById('fileUpload-' + this.props.submitButtonLabel) as HTMLInputElement
        if (fileUpload) {
            fileUpload.click()
        }
    }

    handleChangeFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {commentFileList} = this.props
            const fileName = event.target.files[0].name
            const fileSize = event.target.files[0].size
            let inputError: InputError | undefined
            if (fileSize > HKConstants.VMN.ASO.MAX_COMMENT_FILE_SIZE * 1024 * 1024) {
                inputError = {
                    title: '資料を追加できませんでした。',
                    messages: [
                        'アップロードの上限サイズ(20MB)を超えています。資料のサイズを確認してください。',
                    ],
                    fileNameList: [
                        `${fileName}(${Math.floor(fileSize / (1024 * 1024))}MB)`,
                    ],
                }
            }
            if (fileSize === 0) {
                inputError = {
                    title: '資料を追加できませんでした。',
                    messages: [
                        '空の資料は追加することができません。資料内容を確認してください。',
                    ],
                    fileNameList: [
                        `${fileName}(0バイト)`,
                    ],
                }
            }
            if (inputError) {
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    inputError,
                )
                if (this.commentFileRef.current) {
                    this.commentFileRef.current.value = ''
                }
                return
            }
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                commentFileList.push({
                    fileNumber: null,
                    fileName: fileName,
                    fileSize: fileSize,
                    fileData: String(reader.result),
                })
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    this.props.inputError,
                )
            }
            reader.onerror = () => {
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    {
                        title: '資料を追加できませんでした。',
                        messages: ['資料の読み込みに失敗しました。もう一度資料を選択してください。'],
                        fileNameList: [`${fileName}`],
                    },
                )
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.commentFileRef.current && this.commentFileRef.current.value) {
            this.commentFileRef.current.value = ''
        }
    }

    handleClickDeleteFileButton(deleteIndex: number): void {
        const {commentFileList, deleteFileNumberList} = this.props
        const newCommentFileList: MeetingCommentFile[] = []
        const newDeleteFileNumberList: number[] = [...deleteFileNumberList]

        for (let index = 0; index < commentFileList.length; index++) {
            const commentFile = commentFileList[index]
            if (index !== deleteIndex) {
                newCommentFileList.push(commentFile)
            } else if (commentFile.fileNumber) {
                newDeleteFileNumberList.push(commentFile.fileNumber)
            }
        }

        this.props.onChange(
            this.props.content,
            newCommentFileList,
            newDeleteFileNumberList,
            this.props.inputError,
        )
    }

    render(): ReactNode {
        const {
            content,
            commentFileList,
            submitButtonLabel,
            onClickSubmitButton,
            onClickCancelButton,
            inputError,
        } = this.props
        const disabledAddFileButton = commentFileList.length >= 5
        const isSubmitDisabled = content.length === 0
        return <>
            <StyledFlexContainer>
                <InputContainer>
                    {
                        inputError &&
                        <ErrorMessageArea>
                            <ErrorMessageTitle>
                                {inputError.title}
                            </ErrorMessageTitle>
                            <Box height={'5px'}/>
                            {inputError.messages.map((message, index) =>
                                <ErrorMessage key={'errorMessage-' + index}>
                                    {message}
                                </ErrorMessage>)}
                            {
                                inputError.fileNameList &&
                                inputError.fileNameList.length > 0 && <>
                                    <Box height={'10px'}/>
                                    {
                                        inputError.fileNameList.map((fileName, index) =>
                                            <ErrorMessage key={'errorFile-' + index}>
                                                {fileName}
                                            </ErrorMessage>)
                                    }
                                </>
                            }
                        </ErrorMessageArea>
                    }
                    <PlainInput
                        inputProps={{
                            minLength: 0,
                            maxLength: 2000,
                        }}
                        maxRows={4.5}
                        value={content}
                        fullWidth
                        multiline
                        autoFocus
                        onChange={this.handleChangeComment}
                    />
                    {
                        commentFileList &&
                        commentFileList.map((commentFile, index) => {
                            return <AttachmentFile key={`commentFile-${index}`}>
                                <RowFlexContainer>
                                    <FullWidthLeftRowFlexContainer>
                                        <IconBox>
                                            {getLinkIconComponent(getFileExtension(commentFile.fileName))}
                                        </IconBox>
                                        <AttachmentFileName>{commentFile.fileName}</AttachmentFileName>
                                        <StyledLink
                                            id={`fileDeleteButton-${index}`}
                                            onClick={() => this.handleClickDeleteFileButton(index)}
                                        >
                                            <FontAwesomeIcon icon={faTimes} color={Colors.AccentDark}/>
                                        </StyledLink>
                                    </FullWidthLeftRowFlexContainer>
                                </RowFlexContainer>
                            </AttachmentFile>
                        })
                    }
                </InputContainer>
                <Box height={'1px'} bgcolor={'#0070B0'}/>
                <Box height={'6px'}/>
                <RowFlexContainer>
                    <FileLinkButton
                        id={'addFileButton'}
                        onClick={this.handleClickAddFileButton}
                        disabled={disabledAddFileButton}
                    >
                        <FontAwesomeIcon icon={faFileMedical} size={'lg'}/>
                    </FileLinkButton>
                    <DisplayNoneFileInput
                        type={'file'}
                        id={'fileUpload-' + submitButtonLabel}
                        ref={this.commentFileRef}
                        onChange={this.handleChangeFileInput}
                    />
                    <RowFlexContainer>
                        {
                            onClickCancelButton &&
                            <>
                                <StyledLinkButton onClick={onClickCancelButton}>
                                    キャンセル
                                </StyledLinkButton>
                                <Box width={'26px'}/>
                            </>
                        }
                        <SubmitButton
                            onClick={onClickSubmitButton}
                            disabled={isSubmitDisabled}
                        >
                            {submitButtonLabel}
                        </SubmitButton>
                    </RowFlexContainer>
                </RowFlexContainer>
                <Box height={'10px'}/>
            </StyledFlexContainer>
        </>
    }
}

export default AdminMeetingCommentInputForm

const DisplayNoneFileInput = styled.input`
  display: none;
`

const StyledFlexContainer = styled(FlexContainer)`
  flex-direction: column;
`
export const ErrorMessageArea = styled.div`
  background: #fbebec;
  padding: 10px;
  margin-bottom: 10px;
  overflow-wrap: anywhere;
`

export const ErrorMessageTitle = styled.div`
  color: ${Colors.Alert};
  font-size: 16px;
  font-weight: ${FontWeights.Bold};
  line-height: 24px;
`

export const ErrorMessage = styled.div`
  font-size: 14px;
  line-height: 21px;
`

const StyledLinkButton = styled.button`
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;

  &:focus {
    outline: 0;
  }

  color: ${Colors.AccentDark};

  :disabled {
    color: ${Colors.AccentLight};
  }
`

const SubmitButton = styled(StyledLinkButton)`
  color: ${Colors.Primary};
`

const InputContainer = styled.label`
  background-color: #F2F8FB;
  padding: 10px 15px;
  border-bottom: 1px solid #F2F8FB;

  &:focus-within {
    border-bottom-color: #0070B0;
  }
`

const PlainInput = withStyles({
    root: {
        background: 'none',
        fontSize: '14px',
        padding: 0,
    },
    input: {
        lineHeight: 1.5,
    },
    notchedOutline: {
        border: 'none',
    },
})
(OutlinedInput)

const FileLinkButton = styled(StyledLinkButton)`
  padding: 1px 6px;
`
